import {
  PRIMARY_ROW_CLASSNAME,
  type EntityAdminBlotterTableApi,
  type EntityAdminClass,
  type EntityAdminRecord,
} from '@talos/kyoko';
import type { RowClassRules } from 'ag-grid-community';
import { SpaceInfluenceType, type HighlightRowSpaceInfluence, type SpaceInfluence } from './types';

function isHighlightRowInfluence(influence: SpaceInfluence | undefined): influence is HighlightRowSpaceInfluence {
  return influence?.type === SpaceInfluenceType.HighlightRow;
}

/**
 * @returns true if the row exists in the grid model, false otherwise
 */
export function applyBlotterSpaceInfluence<TRecord extends EntityAdminRecord>(
  api: EntityAdminBlotterTableApi<TRecord> | null | undefined,
  influence: SpaceInfluence | undefined
): boolean {
  if (api == null || api.gridApi.isDestroyed()) {
    return false;
  }

  if (isHighlightRowInfluence(influence)) {
    return highlightRow(api, influence);
  }

  return false;
}

function highlightRow<TRecord extends EntityAdminRecord>(
  api: EntityAdminBlotterTableApi<TRecord>,
  influence: HighlightRowSpaceInfluence
): boolean {
  const rowExists = api.gridApi.getRowNode(influence.rowId) != null;
  api.gridApi.redrawRows();
  if (rowExists) {
    api.ensureRowVisibleById(influence.rowId, { timeout: 10_000 });
  }
  return rowExists;
}

export function cleanupBlotterSpaceInfluence<TRecord extends EntityAdminRecord>(
  api: EntityAdminBlotterTableApi<TRecord> | null | undefined
) {
  if (api == null || api.gridApi.isDestroyed()) {
    return;
  }

  api.gridApi.redrawRows();
}

export function getSpaceInfluenceRowClassRules<TRecord extends EntityAdminRecord>(
  influence: SpaceInfluence | undefined
): RowClassRules<EntityAdminClass<TRecord>> {
  return {
    [PRIMARY_ROW_CLASSNAME]: ({ node }) =>
      isHighlightRowInfluence(influence) && (node.id === influence.rowId || node.key === influence.rowId),
  };
}

export function subscribeToSpaceInfluenceUpdates<TRecord extends EntityAdminRecord>(
  api: EntityAdminBlotterTableApi<TRecord>,
  applyInfluence: () => boolean | void
) {
  let subscribed = true;
  const unsubscribe = () => {
    if (!subscribed) {
      return;
    }
    subscribed = false;
    api.gridApi.removeEventListener('modelUpdated', handleModelUpdated);
  };
  const handleModelUpdated = () => {
    if (applyInfluence() === true) {
      unsubscribe();
    }
  };

  api.gridApi.addEventListener('modelUpdated', handleModelUpdated);
  return unsubscribe;
}
